import type { TilledPaymentMethodVerifyResponse } from '@bambi/api-client/schemas';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { ClockIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { DateTime } from 'luxon';

import { defaultCurrencyFormat } from 'src/common/defaultCurrencyFormat';

type PaymentMethodVerificationStatusProps = {
  verification: TilledPaymentMethodVerifyResponse | undefined;
  isLoading: boolean;
};

export function PaymentMethodVerificationStatus({
  verification,
  isLoading,
}: PaymentMethodVerificationStatusProps) {
  if (isLoading) {
    return (
      <div className="flex flex-row justify-center">
        <LoadingIndicator
          variant="small"
          testId="loading-payment-method-verification"
        />
      </div>
    );
  }

  if (!verification) {
    return (
      <div className="flex flex-row justify-center gap-1 text-xs text-yellow-500 underline decoration-dashed underline-offset-4">
        Unverified <ExclamationTriangleIcon width={12} />
      </div>
    );
  }

  const formattedDate = DateTime.fromISO(
    verification.updated_at
  ).toLocaleString(DateTime.DATE_SHORT);
  const formattedAmount = defaultCurrencyFormat(verification.amount_cents);

  return (
    <div className="flex flex-row justify-center text-xs">
      {verification.status === 'succeeded' && (
        <SimpleTooltip
          testId="verified-payment-method-tooltip"
          trigger={
            <div className="flex flex-row gap-1 text-green-500">
              Verified <CheckIcon width={12} />
            </div>
          }
          content={
            <>
              We last verified this payment method on {formattedDate} using the
              amount of {formattedAmount}
            </>
          }
        />
      )}
      {verification.status === 'failed' && (
        <SimpleTooltip
          trigger={
            <div className="flex flex-row gap-1 text-red-500 underline decoration-dashed underline-offset-4">
              Verification failed <ExclamationTriangleIcon width={12} />
            </div>
          }
          content={
            <>
              We last verified this payment method on {formattedDate} using the
              amount of {formattedAmount}. This verification failed because of
              the following reason: {verification.failure_message}. Next steps:{' '}
              {verification.next_steps}
            </>
          }
        />
      )}
      {verification.status === 'pending' && (
        <SimpleTooltip
          trigger={
            <div className="flex flex-row gap-1 text-yellow-500">
              Pending <ClockIcon width={12} />
            </div>
          }
          content={
            <>
              We're in the process of verifying this payment method using the
              amount of {formattedAmount}. We started this request on{' '}
              {formattedDate}.
            </>
          }
        />
      )}
    </div>
  );
}
